// src/views/parent/PANotifications.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import { useParentDrawer } from "../../context/ParentDrawerContext";

export default function PANotifications({ slug }) {
  const navigate = useNavigate();
  const { openDrawer } = useParentDrawer();
  const { getNotificationsRecues, marquerNotificationLue } = useParentService();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const hasFetchedRef = useRef(false);
  const markedReadRef = useRef(new Set()); // ids déjà marqués lus

  // ─── CHARGEMENT ──────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!slug) {
      setError("Identifiant parent manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getNotificationsRecues(slug);
      setNotifications(data || []);
    } catch (err) {
      setError("Impossible de récupérer les notifications.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadData();
  }, [loadData]);

  // ─── HELPERS ─────────────────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return `Aujourd'hui à ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
      } else if (diffDays === 1) {
        return `Hier à ${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
      } else {
        return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
      }
    } catch (e) {
      return dateStr;
    }
  };

  const openFile = (url) => {
    if (!url) return;
    window.open(url, "_blank");
  };

  const showNotificationDetail = (notification) => {
    setSelectedNotification(notification);
    setShowModal(true);

    // Marquer comme lue si non lue et pas déjà marquée
    if (!notification.lu && !markedReadRef.current.has(notification.id)) {
      markedReadRef.current.add(notification.id);

      // Mise à jour locale optimiste
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, lu: true } : n))
      );
      setSelectedNotification((prev) =>
        prev && prev.id === notification.id ? { ...prev, lu: true } : prev
      );

      // Appel serveur en arrière-plan
      marquerNotificationLue(slug, notification.id).catch((err) => {
        // Rollback en cas d'échec
        markedReadRef.current.delete(notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, lu: false } : n))
        );
      });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNotification(null);
  };

  // Comptage local des non-lues (basé sur l'état local, plus fiable)
  const unreadCount = notifications.filter((n) => !n.lu).length;

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');

        *, *::before, *::after { box-sizing: border-box; }

        .notif-page {
          min-height: 100vh;
          background: var(--s2);
        }

        .notif-header {
          background: linear-gradient(135deg, var(--pr-dk), var(--pr));
          padding: 52px 16px 40px;
          text-align: center;
          border-radius: 0 0 40px 40px;
          position: relative;
          box-shadow: 0 4px 15px rgba(0,0,0,0.15);
          overflow: hidden;
        }

        .notif-header::after {
          content: "";
          position: absolute;
          left: 20%;
          right: 20%;
          bottom: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, var(--or), transparent);
        }

        .notif-header .menu-btn {
          position: absolute;
          top: 44px;
          left: 16px;
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 12px;
          padding: 8px 10px;
          cursor: pointer;
          color: #fff;
          font-size: 20px;
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
        }

        .notif-header .menu-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .notif-header .icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 10px;
        }

        .notif-header .icon-wrapper i {
          font-size: 24px;
          color: #fff;
        }

        .notif-header h1 {
          font-family: 'Poppins', sans-serif;
          color: #fff;
          font-size: 22px;
          font-weight: 700;
          margin: 0;
        }

        .notif-header p {
          font-family: 'Inter', sans-serif;
          color: rgba(255,255,255,0.7);
          font-size: 14px;
          margin: 8px 0 0;
        }

        .notif-header .count {
          display: inline-block;
          margin-top: 14px;
          padding: 4px 14px;
          background: rgba(255,255,255,0.15);
          border-radius: 20px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 500;
          color: #fff;
        }

        .notif-list {
          padding: 16px;
        }

        .notif-card {
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.06);
          margin-bottom: 12px;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          overflow: hidden;
          position: relative;
        }

        .notif-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.1);
        }

        .notif-card.unread {
          border-left: 4px solid var(--pr);
        }

        .notif-card .content {
          padding: 14px 16px;
          display: flex;
          gap: 14px;
        }

        .notif-card .icon-box {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: var(--pr-bg);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: relative;
        }

        .notif-card .icon-box i {
          font-size: 20px;
          color: var(--pr);
        }

        .notif-card .info {
          flex: 1;
          min-width: 0;
        }

        .notif-card .info .title {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 600;
          color: var(--t);
          margin: 0 0 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .notif-card .info .title .badge {
          display: inline-block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--rd);
          flex-shrink: 0;
        }

        .notif-card .info .message {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
          margin: 0 0 6px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .notif-card .info .meta {
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          color: var(--h);
        }

        .notif-card .info .meta i {
          font-size: 12px;
        }

        .notif-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 20px;
          min-height: 250px;
        }

        .notif-empty i {
          font-size: 64px;
          color: var(--h);
          opacity: 0.4;
        }

        .notif-empty h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 600;
          color: var(--t);
          margin: 16px 0 8px;
        }

        .notif-empty p {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--m);
          text-align: center;
          margin: 0;
        }

        /* Loader intégré */
        .notif-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          gap: 14px;
        }
        .notif-loading .spinner {
          width: 44px;
          height: 44px;
          border: 4px solid var(--pr-bg, #e5eef7);
          border-top-color: var(--pr);
          border-radius: 50%;
          animation: notif-spin 0.8s linear infinite;
        }
        @keyframes notif-spin { to { transform: rotate(360deg); } }
        .notif-loading span {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--m);
        }

        /* Carte d'erreur intégrée */
        .notif-error {
          background: #fff;
          border-radius: 20px;
          padding: 40px 20px;
          text-align: center;
          box-shadow: 0 4px 18px rgba(0,0,0,0.08);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 16px;
        }
        .notif-error i.alert-icon {
          font-size: 48px;
          color: var(--rd);
        }
        .notif-error h2 {
          font-family: 'Poppins', sans-serif;
          font-size: 17px; font-weight: 600;
          color: var(--t); margin: 4px 0 0;
        }
        .notif-error p {
          font-family: 'Inter', sans-serif;
          font-size: 13px; color: var(--m);
          margin: 0 0 12px;
        }
        .notif-error .actions-row {
          display: flex; gap: 8px;
          flex-wrap: wrap; justify-content: center;
        }
        .notif-error .btn {
          border: none; border-radius: 30px;
          padding: 10px 22px; cursor: pointer;
          font-family: 'Inter', sans-serif;
          font-size: 13px; font-weight: 600;
          color: #fff; background: var(--pr);
          display: inline-flex; align-items: center; gap: 6px;
          transition: opacity 0.2s;
        }
        .notif-error .btn:hover { opacity: 0.9; }
        .notif-error .btn.btn-secondary {
          background: var(--b2); color: var(--t);
        }

        /* Modal */
        .notif-modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: flex-end;
          justify-content: center;
        }

        .notif-modal {
          background: #fff;
          border-radius: 25px 25px 0 0;
          width: 100%;
          max-width: 430px;
          max-height: 80vh;
          overflow-y: auto;
          padding: 20px 24px 28px;
        }

        .notif-modal .handle {
          width: 40px;
          height: 4px;
          background: var(--b);
          border-radius: 2px;
          margin: 0 auto 20px;
        }

        .notif-modal .icon-box {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          background: var(--pr-bg);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
        }

        .notif-modal .icon-box i {
          font-size: 24px;
          color: var(--pr);
        }

        .notif-modal .title {
          font-family: 'Poppins', sans-serif;
          font-size: 20px;
          font-weight: 700;
          color: var(--t);
          margin: 0 0 4px;
        }

        .notif-modal .date {
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          color: var(--h);
          margin: 0 0 16px;
        }

        .notif-modal .divider {
          border: none;
          border-top: 1px solid var(--b);
          margin: 0 0 16px;
        }

        .notif-modal .message {
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          color: var(--t);
          line-height: 1.5;
          margin: 0 0 16px;
        }

        .notif-modal .file-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: var(--pr-bg);
          border: 1px solid rgba(27,79,128,0.2);
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.2s;
          width: 100%;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--pr);
        }

        .notif-modal .file-btn:hover {
          background: rgba(27,79,128,0.12);
        }

        .notif-modal .file-btn i {
          font-size: 18px;
        }

        @media (max-width: 480px) {
          .notif-header {
            padding: 44px 16px 32px;
          }
          .notif-header h1 {
            font-size: 19px;
          }
        }
      `}</style>

      <div className="notif-page">
        {/* Header TOUJOURS affiché */}
        <div className="notif-header">
          <button className="menu-btn" onClick={openDrawer} aria-label="Ouvrir le menu">
            <i className="ti ti-menu-2" />
          </button>

          <div className="icon-wrapper">
            <i className="ti ti-bell" />
          </div>
          <h1>Notifications</h1>
          <p>Restez informé des actualités</p>
          {!loading && !error && notifications.length > 0 && (
            <div className="count">
              {notifications.length} notification{notifications.length > 1 ? 's' : ''}
              {unreadCount > 0 && ` · ${unreadCount} non lue${unreadCount > 1 ? 's' : ''}`}
            </div>
          )}
        </div>

        {loading ? (
          /* ── ÉTAT CHARGEMENT ── */
          <div className="notif-loading">
            <div className="spinner" />
            <span>Chargement des notifications…</span>
          </div>
        ) : error ? (
          /* ── ÉTAT ERREUR ── */
          <div className="notif-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <div className="actions-row">
              <button className="btn" onClick={loadData}>
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/${slug}/parent/accueil`)}
              >
                <i className="ti ti-home" />
                Accueil
              </button>
            </div>
          </div>
        ) : (
          /* ── CONTENU ── */
          <div className="notif-list">
            {notifications.length === 0 ? (
              <div className="notif-empty">
                <i className="ti ti-bell" />
                <h2>Aucune notification</h2>
                <p>Vous serez notifié dès qu'une nouvelle information sera disponible</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`notif-card ${!notif.lu ? "unread" : ""}`}
                  onClick={() => showNotificationDetail(notif)}
                >
                  <div className="content">
                    <div className="icon-box">
                      <i className="ti ti-bell" />
                    </div>
                    <div className="info">
                      <h3 className="title">
                        {!notif.lu && <span className="badge" />}
                        {notif.titre || "Notification"}
                      </h3>
                      <p className="message">{notif.message || ""}</p>
                      <div className="meta">
                        <i className="ti ti-clock" />
                        <span>{formatDate(notif.date_envoi)}</span>
                        {notif.fichier && (
                          <>
                            <i className="ti ti-paperclip" />
                            <span>Pièce jointe</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modal Détails */}
      {showModal && selectedNotification && (
        <div className="notif-modal-overlay" onClick={closeModal}>
          <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
            <div className="handle" />

            <div className="icon-box">
              <i className="ti ti-bell" />
            </div>

            <h2 className="title">{selectedNotification.titre || "Notification"}</h2>
            <p className="date">{formatDate(selectedNotification.date_envoi)}</p>

            <hr className="divider" />

            <p className="message">{selectedNotification.message || ""}</p>

            {selectedNotification.fichier && (
              <>
                <hr className="divider" />
                <button className="file-btn" onClick={() => openFile(selectedNotification.fichier)}>
                  <i className="ti ti-file" />
                  <span>Ouvrir le document</span>
                  <i className="ti ti-external-link" style={{ marginLeft: 'auto' }} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}